// Infrastructure provider: real building footprints and critical facilities
// from OpenStreetMap via Overpass. Replaces the population-density-PNG heuristic.
//
// Performance strategy (dense metro areas are heavy for public Overpass servers):
//   1. The bbox is split into 2x2 quadrants fetched IN PARALLEL, each starting
//      on a different Overpass mirror (mirrors throttle per IP, so spreading
//      the load beats queueing on one server). Each quadrant streams into the
//      shared grids via `onIncrement` as it lands, so the map fills in live.
//   2. Buildings come back as bare centroids (`out ids center qt` — compact).
//   3. Critical facilities arrive in a second, tiny, tagged query.
//   4. If every quadrant fails, fall back to land-use polygons rasterized
//      onto the grid with an estimated structure density.
//
// Output per grid cell:
//   structureCount — number of structures in the cell
//   structureValue — weighted "value" (critical facilities count for more),
//                    used by the damage forecaster to rank losses

import {
  latLonToCell,
  isInBounds,
  pointInRing,
  ringBbox,
  cellCenterLatLon,
} from "./geo";
import { overpassQuery } from "./overpass";

// Relative protection value per structure type
const VALUE_WEIGHTS = {
  hospital: 25,
  clinic: 10,
  school: 10,
  kindergarten: 8,
  fire_station: 15,
  police: 12,
  substation: 20,
  plant: 25,
  default: 1,
};

const LANDUSE_DENSITY_PER_CELL = 6; // estimated structures/cell for landuse fallback

const classifyValue = (tags = {}) => {
  if (tags.amenity && VALUE_WEIGHTS[tags.amenity])
    return VALUE_WEIGHTS[tags.amenity];
  if (tags.power && VALUE_WEIGHTS[tags.power]) return VALUE_WEIGHTS[tags.power];
  return VALUE_WEIGHTS.default;
};

const emptyGrids = (dims) => ({
  structureCount: Array(dims.rows)
    .fill(null)
    .map(() => new Uint16Array(dims.cols)),
  structureValue: Array(dims.rows)
    .fill(null)
    .map(() => new Float32Array(dims.cols)),
  totalStructures: 0,
  totalValue: 0,
});

const addPoint = (grids, bbox, dims, lat, lon, value) => {
  const { row, col } = latLonToCell(bbox, dims, lat, lon);
  if (!isInBounds(row, col, dims.rows, dims.cols)) return;
  grids.structureCount[row][col] += 1;
  grids.structureValue[row][col] += value;
  grids.totalStructures += 1;
  grids.totalValue += value;
};

const bboxStr = (b) => `${b.south},${b.west},${b.north},${b.east}`;

const splitBbox = (bbox, nx = 2, ny = 2) => {
  const quads = [];
  const dLat = (bbox.north - bbox.south) / ny;
  const dLon = (bbox.east - bbox.west) / nx;
  for (let y = 0; y < ny; y++) {
    for (let x = 0; x < nx; x++) {
      quads.push({
        south: bbox.south + y * dLat,
        north: bbox.south + (y + 1) * dLat,
        west: bbox.west + x * dLon,
        east: bbox.west + (x + 1) * dLon,
      });
    }
  }
  return quads;
};

// Fallback: rasterize land-use polygons to an estimated structure density
const fetchLanduseFallback = async (bbox, dims, grids, onProgress) => {
  onProgress?.("Buildings unavailable — using land-use areas (OSM)…");
  const json = await overpassQuery(`
    [out:json][timeout:60];
    way["landuse"~"residential|commercial|industrial|retail"](${bboxStr(bbox)});
    out geom qt;`);
  const rings = (json.elements || [])
    .filter((el) => el.type === "way" && el.geometry?.length >= 3)
    .map((el) => el.geometry.map((g) => ({ lat: g.lat, lon: g.lon })));
  for (const ring of rings) {
    const rb = ringBbox(ring);
    for (let r = 0; r < dims.rows; r++) {
      for (let c = 0; c < dims.cols; c++) {
        if (grids.structureCount[r][c] > 0) continue;
        const { lat, lon } = cellCenterLatLon(bbox, dims, r, c);
        if (lat < rb.south || lat > rb.north || lon < rb.west || lon > rb.east)
          continue;
        if (pointInRing(lat, lon, ring)) {
          grids.structureCount[r][c] = LANDUSE_DENSITY_PER_CELL;
          grids.structureValue[r][c] = LANDUSE_DENSITY_PER_CELL;
          grids.totalStructures += LANDUSE_DENSITY_PER_CELL;
          grids.totalValue += LANDUSE_DENSITY_PER_CELL;
        }
      }
    }
  }
};

// onIncrement is called after each quadrant (or facility batch) lands, with
// the shared grids object — callers can re-render partial infrastructure.
export const fetchInfrastructure = async (
  bbox,
  dims,
  onProgress,
  onIncrement
) => {
  const grids = emptyGrids(dims);
  let source = "OpenStreetMap buildings (Overpass)";

  // 1. Building centroids (2x2 quadrants) and critical facilities all fire
  //    in parallel, spread across mirrors — total wall clock is the slowest
  //    single request instead of the sum.
  const quads = splitBbox(bbox);
  onProgress?.(`Fetching buildings (0/${quads.length} regions)…`);
  let doneQuads = 0;
  const quadPromises = quads.map((qb, i) =>
    overpassQuery(
      `[out:json][timeout:60];way["building"](${bboxStr(qb)});out ids center qt;`,
      { endpointOffset: i, retries: 1 }
    ).then((json) => {
      for (const el of json.elements || []) {
        const lat = el.center?.lat ?? el.lat;
        const lon = el.center?.lon ?? el.lon;
        if (lat !== undefined) addPoint(grids, bbox, dims, lat, lon, 1);
      }
      doneQuads++;
      onProgress?.(
        `Fetching buildings (${doneQuads}/${quads.length} regions, ${grids.totalStructures.toLocaleString()} mapped)…`
      );
      onIncrement?.(grids);
    })
  );

  // Critical facilities: tiny query, short timeout, failure is non-fatal
  const facilitiesPromise = overpassQuery(
    `
    [out:json][timeout:25];
    (
      nwr["amenity"~"hospital|clinic|school|kindergarten|fire_station|police"](${bboxStr(bbox)});
      nwr["power"~"substation|plant"](${bboxStr(bbox)});
    );
    out center tags qt;`,
    { retries: 0, endpointOffset: 1, timeoutMs: 45000 }
  )
    .then((json) => {
      for (const el of json.elements || []) {
        const lat = el.center?.lat ?? el.lat;
        const lon = el.center?.lon ?? el.lon;
        if (lat === undefined) continue;
        const value = classifyValue(el.tags);
        if (value > 1) addPoint(grids, bbox, dims, lat, lon, value);
      }
      onIncrement?.(grids);
    })
    .catch((err) =>
      console.warn("Critical-facility query failed (non-fatal):", err)
    );

  const results = await Promise.allSettled(quadPromises);
  const failures = results.filter((r) => r.status === "rejected").length;

  if (failures === quads.length) {
    // Nothing came back — try the lighter land-use estimate
    try {
      await fetchLanduseFallback(bbox, dims, grids, onProgress);
      source = "OpenStreetMap land-use estimate (Overpass)";
      onIncrement?.(grids);
    } catch (err) {
      console.warn("Land-use fallback also failed:", err);
      throw new Error(
        "Could not load infrastructure data from OpenStreetMap. " +
          "Overpass servers may be busy — try again in a minute or pick a smaller region."
      );
    }
  } else if (failures > 0) {
    console.warn(`${failures}/${quads.length} building quadrants failed.`);
    source += ` (${quads.length - failures}/${quads.length} regions)`;
  }

  await facilitiesPromise;

  return { ...grids, source, failedRegions: failures === quads.length ? 0 : failures };
};
